import React from 'react'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { AuthProvider, useAuth } from './context/AuthContext'
import { RequireAuth, RequireAdmin } from './components/RouteGuards'
import AppLayout from './components/AppLayout'
import LoginPage from './pages/LoginPage'
import DashboardPage from './pages/DashboardPage'
import ClientsPage from './pages/ClientsPage'
import ClientDetailPage from './pages/ClientDetailPage'
import { LeadsPage, LeadDetailPage } from './pages/LeadsPage'
import InventoryPage from './pages/InventoryPage'
import QuotesPage from './pages/QuotesPage'
import QuoteDetailPage from './pages/QuoteDetailPage'
import JobsPage from './pages/JobsPage'
import JobDetailPage from './pages/JobDetailPage'
import StaffPage from './pages/StaffPage'
import MyJobsPage from './pages/MyJobsPage'
import ReportsPage from './pages/ReportsPage'
import PublicIntakeForm from './pages/PublicIntakeForm'
import PublicQuoteAcceptPage from './pages/PublicQuoteAcceptPage'
import PublicStaffResponsePage from './pages/PublicStaffResponsePage'
import HelpPage from './pages/HelpPage'

import './styles/globals.css'

// Smart root redirect: admins go to /dashboard, labor users go to /my-jobs
function RoleRedirect() {
  const { isAdmin, user, loading } = useAuth()

  if (loading || !user) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--color-text-muted)' }}>
        Loading…
      </div>
    )
  }

  return <Navigate to={isAdmin ? '/dashboard' : '/my-jobs'} replace />
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          {/* Public routes — no auth required */}
          <Route path="/login"                          element={<LoginPage />} />
          <Route path="/inquiry"                        element={<PublicIntakeForm />} />
          <Route path="/quote-accept/:token"            element={<PublicQuoteAcceptPage />} />
          <Route path="/staff-respond/:token/:response" element={<PublicStaffResponsePage />} />

          {/* Protected app shell */}
          <Route path="/" element={<RequireAuth><AppLayout /></RequireAuth>}>

            {/* Admin-only routes */}
            <Route path="dashboard"   element={<RequireAdmin><DashboardPage /></RequireAdmin>} />
            <Route path="leads"       element={<RequireAdmin><LeadsPage /></RequireAdmin>} />
            <Route path="leads/:id"   element={<RequireAdmin><LeadDetailPage /></RequireAdmin>} />
            <Route path="clients"     element={<RequireAdmin><ClientsPage /></RequireAdmin>} />
            <Route path="clients/:id" element={<RequireAdmin><ClientDetailPage /></RequireAdmin>} />
            <Route path="inventory"   element={<RequireAdmin><InventoryPage /></RequireAdmin>} />
            <Route path="quotes"      element={<RequireAdmin><QuotesPage /></RequireAdmin>} />
            <Route path="quotes/new"  element={<RequireAdmin><QuoteDetailPage /></RequireAdmin>} />
            <Route path="quotes/:id"  element={<RequireAdmin><QuoteDetailPage /></RequireAdmin>} />
            <Route path="jobs"        element={<RequireAdmin><JobsPage /></RequireAdmin>} />
            <Route path="jobs/new"    element={<RequireAdmin><JobDetailPage /></RequireAdmin>} />
            <Route path="jobs/:id"    element={<RequireAdmin><JobDetailPage /></RequireAdmin>} />
            <Route path="staff"       element={<RequireAdmin><StaffPage /></RequireAdmin>} />
            <Route path="reports"     element={<RequireAdmin><ReportsPage /></RequireAdmin>} />
            <Route path="help"        element={<RequireAdmin><HelpPage /></RequireAdmin>} />

            {/* Labor route — all authenticated users */}
            <Route path="my-jobs" element={<RequireAuth><MyJobsPage /></RequireAuth>} />

            {/* Root index — role-aware redirect */}
            <Route index element={<RoleRedirect />} />
          </Route>

          {/* Catch-all — redirect to root (which triggers RoleRedirect) */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  )
}
